import React from 'react';
import { MegaMenu } from '../MegaMenu/MegaMenu';
import type { MegaMenuProps, PrimaryNavGroup, SecondaryNavGroup, NavLink, NavFooterItem } from '../MegaMenu/MegaMenu';
import type { ColumnVariant } from '../MegaMenu/PanelLayout';

/**
 * MegaMenuBlock - CMS wrapper that maps flat fields to MegaMenu's structured props
 *
 * Poly CMS block templates use flat key-value fields. This component accepts those
 * flat fields and assembles them into the nested PrimaryNavGroup[] structure that
 * MegaMenu expects.
 *
 * Limits: 7 nav groups × 4 panels × 8 links + 4 footer items
 */

// ── Constants ────────────────────────────────────────────────────────────────
const MAX_NAV_GROUPS = 7;
const MAX_PANELS = 4;
const MAX_LINKS = 8;
const MAX_FOOTER_ITEMS = 4;

// ── Flat Props Interface ─────────────────────────────────────────────────────

/** Per-link flat fields */
type NavLinkFields = {
  [K in `nav${1 | 2 | 3 | 4 | 5 | 6 | 7}Panel${1 | 2 | 3 | 4}Link${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8}${'Title' | 'Url' | 'Id' | 'IconUrl' | 'IconAlt' | 'BadgeText' | 'Description'}`]?: string;
};

/** Per-panel flat fields */
type NavPanelFields = {
  [K in `nav${1 | 2 | 3 | 4 | 5 | 6 | 7}Panel${1 | 2 | 3 | 4}${'Title' | 'Variant' | 'ColumnType' | 'ActionText' | 'ActionUrl' | 'ActionCtaId'}`]?: string;
};

/** Per-nav-group flat fields (strings) */
type NavGroupStringFields = {
  [K in `nav${1 | 2 | 3 | 4 | 5 | 6 | 7}${'Title' | 'Id' | 'DirectLinkUrl' | 'DirectLinkCtaId' | 'FeaturedImageUrl' | 'FeaturedImageAlt' | 'FeaturedUrl' | 'FeaturedId' | 'FeaturedTitle' | 'FeaturedDescription' | 'FeaturedBadgeText' | 'FeaturedBadgeIconUrl' | 'FeaturedBadgeIconAlt' | 'FeaturedPanelTitle' | 'FeaturedSize'}`]?: string;
};


/** Per-nav-group flat fields (booleans) */
type NavGroupBoolFields = {
  [K in `nav${1 | 2 | 3 | 4 | 5 | 6 | 7}${'IsDirectLink' | 'HasFeaturedPanel'}`]?: boolean;
};

/** Nav footer flat fields */
type NavFooterFields = {
  [K in `navFooter${1 | 2 | 3 | 4}${'Id' | 'Title' | 'Text' | 'Url' | 'Target'}`]?: string;
};

/**
 * @polySemanticUsage About this block: the persistent global navigation bar at the top of every
 * page. Contains the primary site logo, top-level menu links, utility links (e.g. sign in,
 * language selector), and the primary CTA. Appears once per page; never duplicate it or nest it
 * within content areas.
 */
export interface MegaMenuBlockProps extends
  Omit<MegaMenuProps, 'primaryNavGroups' | 'navFooterItems'>,
  NavLinkFields,
  NavPanelFields,
  NavGroupStringFields,
  NavGroupBoolFields,
  NavFooterFields {}

// ── Mapping Logic ────────────────────────────────────────────────────────────

function getField(props: Record<string, unknown>, key: string): string | undefined {
  const val = props[key];
  return typeof val === 'string' && val.length > 0 ? val : undefined;
}

function getBoolField(props: Record<string, unknown>, key: string): boolean | undefined {
  const val = props[key];
  if (typeof val === 'boolean') return val;
  if (val === 'true') return true;
  if (val === 'false') return false;
  return undefined;
}

function buildNavLink(props: Record<string, unknown>, prefix: string): NavLink | null {
  const title = getField(props, `${prefix}Title`);
  const url = getField(props, `${prefix}Url`);
  if (!title || !url) return null;
  return {
    title,
    url,
    id: getField(props, `${prefix}Id`),
    iconUrl: getField(props, `${prefix}IconUrl`),
    iconAlt: getField(props, `${prefix}IconAlt`) ?? '',
    badgeText: getField(props, `${prefix}BadgeText`),
    description: getField(props, `${prefix}Description`),
  };
}

function buildSecondaryNavGroup(props: Record<string, unknown>, prefix: string): SecondaryNavGroup | null {
  const title = getField(props, `${prefix}Title`);
  if (!title) return null;

  const links: NavLink[] = [];
  for (let l = 1; l <= MAX_LINKS; l++) {
    const link = buildNavLink(props, `${prefix}Link${l}`);
    if (link) links.push(link);
  }
  if (links.length === 0) return null;

  return {
    title,
    variant: (getField(props, `${prefix}Variant`) as ColumnVariant) || 'with-icons',
    columnType: (getField(props, `${prefix}ColumnType`) as 'single' | 'dual') || 'single',
    actionText: getField(props, `${prefix}ActionText`),
    actionUrl: getField(props, `${prefix}ActionUrl`),
    actionCtaId: getField(props, `${prefix}ActionCtaId`),
    links,
  };
}

function buildPrimaryNavGroup(props: Record<string, unknown>, n: number): PrimaryNavGroup | null {
  const prefix = `nav${n}`;
  const title = getField(props, `${prefix}Title`);
  if (!title) return null;

  const secondaryNavGroups: SecondaryNavGroup[] = [];
  for (let p = 1; p <= MAX_PANELS; p++) {
    const panel = buildSecondaryNavGroup(props, `${prefix}Panel${p}`);
    if (panel) secondaryNavGroups.push(panel);
  }

  const isDirectLink = getBoolField(props, `${prefix}IsDirectLink`);

  return {
    title,
    id: getField(props, `${prefix}Id`) || `nav-${n}`,
    isDirectLink: isDirectLink ?? undefined,
    directLinkUrl: getField(props, `${prefix}DirectLinkUrl`),
    directLinkCtaId: getField(props, `${prefix}DirectLinkCtaId`),
    hasFeaturedPanel: getBoolField(props, `${prefix}HasFeaturedPanel`) ?? undefined,
    featuredImageUrl: getField(props, `${prefix}FeaturedImageUrl`),
    featuredImageAlt: getField(props, `${prefix}FeaturedImageAlt`) ?? getField(props, `${prefix}FeaturedTitle`) ?? (getField(props, `${prefix}FeaturedImageUrl`) ? 'Featured image' : ''),
    featuredUrl: getField(props, `${prefix}FeaturedUrl`),
    featuredId: getField(props, `${prefix}FeaturedId`),
    featuredTitle: getField(props, `${prefix}FeaturedTitle`),
    featuredDescription: getField(props, `${prefix}FeaturedDescription`),
    featuredBadgeText: getField(props, `${prefix}FeaturedBadgeText`),
    featuredBadgeIconUrl: getField(props, `${prefix}FeaturedBadgeIconUrl`),
    featuredBadgeIconAlt: getField(props, `${prefix}FeaturedBadgeIconAlt`) ?? '',
    featuredPanelTitle: getField(props, `${prefix}FeaturedPanelTitle`),
    featuredSize: getField(props, `${prefix}FeaturedSize`) as 'sm' | 'lg' | undefined,
    secondaryNavGroups,
  };
}

function buildNavFooterItems(props: Record<string, unknown>): NavFooterItem[] {
  const items: NavFooterItem[] = [];
  for (let i = 1; i <= MAX_FOOTER_ITEMS; i++) {
    const prefix = `navFooter${i}`;
    const title = getField(props, `${prefix}Title`);
    const url = getField(props, `${prefix}Url`);
    if (!title || !url) continue;
    items.push({
      id: getField(props, `${prefix}Id`) || `footer-${i}`,
      title,
      text: getField(props, `${prefix}Text`),
      url,
      target: getField(props, `${prefix}Target`) as '_blank' | '_self' | undefined,
    });
  }
  return items;
}

export function mapFlatPropsToMegaMenu(props: MegaMenuBlockProps): MegaMenuProps {
  const record = props as unknown as Record<string, unknown>;

  const primaryNavGroups: PrimaryNavGroup[] = [];
  for (let n = 1; n <= MAX_NAV_GROUPS; n++) {
    const group = buildPrimaryNavGroup(record, n);
    if (group) primaryNavGroups.push(group);
  }

  const navFooterItems = buildNavFooterItems(record);

  const NAV_FIELD_PATTERN = /^nav(\d|Footer)/;
  const rest = Object.fromEntries(
    Object.entries(props).filter(([key]) => !NAV_FIELD_PATTERN.test(key))
  ) as Omit<MegaMenuProps, 'primaryNavGroups' | 'navFooterItems'>;

  return {
    ...rest,
    primaryNavGroups: primaryNavGroups.length > 0 ? primaryNavGroups : undefined,
    navFooterItems: navFooterItems.length > 0 ? navFooterItems : undefined,
  };
}

// ── Component ────────────────────────────────────────────────────────────────

export const MegaMenuBlock: React.FC<MegaMenuBlockProps> = (props) => {
  const megaMenuProps = mapFlatPropsToMegaMenu(props);
  return <MegaMenu {...megaMenuProps} />;
};
